import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import { useMediaUpload } from '@/pages/AdvancedEditor/hooks/media/useMediaUpload';
import styled from '@emotion/styled';
import { yupResolver } from '@hookform/resolvers/yup';
import { CircularProgress } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useFieldArray, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { FigmaImporter } from './figma';

const FigmaModalContainer = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    z-index: 1000;
    width: 80%;
    height: 80%;
    background-color: white;
    transform: translate(-50%, -50%);
    top: 50%;
    left: 50%;
    padding: 20px;
    border-radius: 10px;
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.5);

    display: flex;
    flex-direction: column;
    gap: 20px;
`;

export interface FigmaModalFormData {
    fileKey: string;
    scenes: {
        nodeId: string;
        script: string;
    }[];
}

const validationSchema = yup.object({
    fileKey: yup.string().required('File key is required'),
    scenes: yup
        .array()
        .of(
            yup.object({
                nodeId: yup.string().required('Node ID is required'),
                script: yup.string().required('Script is required'),
            })
        )
        .required(),
});

export const FigmaModal = observer(({ onClose }: { onClose: () => void }) => {
    const { store } = useAdvancedEditorContext();
    const {
        control,
        register,
        setValue,
        handleSubmit,
        formState: { isSubmitting, errors },
    } = useForm<FigmaModalFormData>({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            fileKey: '',
            scenes: [],
        },
        mode: 'all',
    });
    const { mutateAsync: uploadMedia } = useMediaUpload();

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'scenes',
    });

    const onSubmit = async (data: FigmaModalFormData) => {
        const figmaImporter = new FigmaImporter(store, uploadMedia);
        await figmaImporter.import(data);
    };

    const handleUrlChange = (url: string) => {
        const parts = new URL(url).pathname.split('/');
        return parts[2] || '';
    };

    const handleNodeIdChange = (url: string, cb: (nodeId: string) => void) => {
        const urlObj = new URL(url);
        const nodeId = urlObj.searchParams.get('node-id');
        cb(nodeId || '');
    };

    const handleScriptChange = (value: string, cb: (script: string) => void) => {
        cb(value.trim());
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <FigmaModalContainer>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <input
                        {...register('fileKey')}
                        placeholder="Figma URL"
                        onChange={e => setValue('fileKey', handleUrlChange(e.target.value))}
                    />
                    {errors.fileKey && <p>{errors.fileKey.message}</p>}
                </div>

                <br />
                {fields.map((field, index) => (
                    <div key={field.id} style={{ display: 'flex', gap: '10px', width: '100%' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <input
                                {...register(`scenes.${index}.nodeId`)}
                                placeholder="Node ID"
                                onChange={e => handleNodeIdChange(e.target.value, nodeId => setValue(`scenes.${index}.nodeId`, nodeId))}
                            />
                            {errors.scenes?.[index]?.nodeId && <p>{errors.scenes[index].nodeId.message}</p>}
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <textarea
                                {...register(`scenes.${index}.script`)}
                                placeholder="Script"
                                style={{ flex: 1 }}
                                onChange={e => handleScriptChange(e.target.value, script => setValue(`scenes.${index}.script`, script))}
                            />
                            {errors.scenes?.[index]?.script && <p>{errors.scenes[index].script.message}</p>}
                        </div>

                        <button onClick={() => remove(index)}>Remove scene</button>
                    </div>
                ))}

                <div style={{ display: 'flex', gap: '10px' }}>
                    <button disabled={isSubmitting} onClick={() => append({ nodeId: '', script: '' })}>
                        + Add scene
                    </button>

                    <button disabled={isSubmitting} type="submit">
                        {isSubmitting ? (
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                Processing...
                                <CircularProgress size={16} />
                            </div>
                        ) : (
                            'Submit'
                        )}
                    </button>
                    <button disabled={isSubmitting} onClick={onClose}>
                        Close
                    </button>
                </div>
            </FigmaModalContainer>
        </form>
    );
});
